import { useEffect, useState } from 'react'

import { useRevalidator, useFetcher } from '@remix-run/react'

import type {
  MessageProps,
  MessageType,
} from '@web/components/message/messages'

export const usePendingMessages = (existingMessages: MessageProps[] = []) => {
  const { formData } = useFetcher({ key: 'sendChat' })
  const content = formData?.get('message') as string
  const type = (formData?.get('type') ?? 'CUSTOMER') as
    | 'CUSTOMER'
    | 'INTERNAL_USER_NOTE'
  const customerFirstName = formData?.get('customerFirstName') as string
  const customerLastName = formData?.get('customerLastName') as string
  const messages = content
    ? existingMessages.concat([
        generateNewMessage(
          content,
          type,
          customerFirstName,
          customerLastName,
        ) as MessageProps,
      ])
    : existingMessages

  return messages
}

const generateNewMessage = (
  content: string,
  messageType: 'CUSTOMER' | 'INTERNAL_USER_NOTE',
  firstName: string,
  lastName: string,
): MessageProps => {
  return {
    id: 'stub',
    content,
    customer: {
      email: 'stub',
      firstName,
      lastName,
    },
    messageType,
    createdAt: new Date().toISOString(),
  }
}

// Currently, getByIdForQ may take >2 seconds in prod for subscribers with many customers (e.g. hundreds of thousands).
// If getByIdForQ takes longer than the polling interval, the request is cancelled and polling appears broken.
const ACTIVE_POLLING_INTERVAL = 5_000
const IDLE_POLLING_INTERVAL = 30_000
const POLLING_TIMEOUT = 120_000
const BOT_MESSAGE_TYPES: MessageType[] = ['BOT_RESPONSE', 'PENDING_RESPONSE']

export const usePoll = (
  messages: MessageProps[],
  options?: {
    // How long in ms to poll when the connection is active.
    activePollingInterval?: number
    // How long in ms to poll when the connection is idle.
    idlePollingInterval?: number
    // How long in ms to wait after the last message from the bot before we consider the connection idle.
    activePollingBuffer?: number
  },
) => {
  const {
    activePollingInterval = ACTIVE_POLLING_INTERVAL,
    idlePollingInterval = IDLE_POLLING_INTERVAL,
    activePollingBuffer = 0,
  } = options ?? {}
  const revalidator = useRevalidator()

  const [pollingState, setPollingState] = useState<
    'active' | 'idle' | 'timeout'
  >('idle')

  // Used to handle when a new message is added after a timeout.
  const [prevNumMessages, setNumMessages] = useState(messages.length)

  // More messages will be sent after an internal model note.
  // Therefore, they are not terminal messages.
  const terminalMessages = (Array.isArray(messages) ? messages : []).filter(
    ({ messageType }) => messageType !== 'INTERNAL_MODEL_NOTE',
  )
  const finalMessage = terminalMessages.slice(-1)[0]

  const lastMessageIsNotBot =
    finalMessage && !BOT_MESSAGE_TYPES.includes(finalMessage.messageType)
  // In fast chat, bot may respond with multiple messages in succession, give it a buffer
  const hasCustomerMessage = messages.some(
    ({ messageType }) => messageType === 'CUSTOMER',
  )
  const botMayStillBeResponding =
    hasCustomerMessage &&
    !!finalMessage &&
    !!finalMessage.createdAt &&
    BOT_MESSAGE_TYPES.includes(finalMessage.messageType) &&
    Date.now() - new Date(finalMessage.createdAt).getTime() <
      activePollingBuffer

  const waitingOnBot = lastMessageIsNotBot || botMayStillBeResponding

  useEffect(() => {
    if (messages.length > prevNumMessages) {
      if (pollingState === 'timeout') {
        // Reset the polling state not to timeout.
        setPollingState(waitingOnBot ? 'active' : 'idle')
      }
      setNumMessages(messages.length)
    }
  }, [messages.length, pollingState, prevNumMessages, waitingOnBot])

  useEffect(() => {
    if (pollingState === 'timeout') {
      return
    }

    const timeout = setTimeout(() => {
      setPollingState('timeout')
    }, POLLING_TIMEOUT)

    return () => clearTimeout(timeout)
  }, [pollingState])

  useEffect(() => {
    const pollingDelay =
      pollingState === 'active' ? activePollingInterval : idlePollingInterval

    const interval = setInterval(() => {
      if (pollingState !== 'timeout') {
        revalidator.revalidate()
      }
    }, pollingDelay)

    return () => clearInterval(interval)
  }, [pollingState, revalidator, activePollingInterval, idlePollingInterval])

  useEffect(() => {
    if (pollingState === 'active' && !waitingOnBot) {
      setPollingState('idle')
    } else if (pollingState === 'idle' && waitingOnBot) {
      setPollingState('active')
    }
  }, [pollingState, waitingOnBot])

  return pollingState
}
